import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send, Loader2, CheckCircle2, Copy, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const budgets = ["< $5k", "$5k – $15k", "$15k – $50k", "$50k+", "Let's discuss"];

export default function Contact({ profile }) {
    const [form, setForm] = useState({ name: "", email: "", company: "", budget: "", subject: "", message: "", hp: "" });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill name, email, and message.");
            return;
        }
        setLoading(true);
        try {
            const res = await api.post("/contact", form);
            setDone(true);
            toast.success(
                res.data?.email_sent
                    ? "Message sent. I'll reply to your email soon."
                    : "Message received. I'll get back to you soon."
            );
            setForm({ name: "", email: "", company: "", budget: "", subject: "", message: "", hp: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32 border-t border-white/5">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-brand/10 blur-[140px] pointer-events-none" aria-hidden />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-5 space-y-10">
                        <div>
                            <div className="mono-label mb-4">05 · Contact</div>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tighter">
                                Let&rsquo;s build
                                <br />
                                something <span className="text-brand">special</span>.
                            </h2>
                            <p className="mt-6 text-ink-200 max-w-md leading-relaxed">
                                Open for new project inquiries, system architecture consultations, and engineering collaborations.
                                Send a message and I will reply within 24 hours.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <ContactRow icon={Mail} label="Email" value={profile?.email} href={`mailto:${profile?.email}`} testid="contact-email" copyable />
                            {profile?.phone && (
                                <ContactRow icon={Phone} label="Phone" value={profile.phone} href={`tel:${profile.phone}`} testid="contact-phone" copyable />
                            )}
                            <ContactRow icon={MapPin} label="Based in" value={profile?.location} testid="contact-location" />
                        </div>

                        <div className="pt-6 border-t border-white/5 grid grid-cols-2 gap-4">
                            {Object.entries(profile?.social || {})
                                .filter(([, v]) => v)
                                .map(([k, v]) => {
                                    const href = v.startsWith("http")
                                        ? v
                                        : k === "discord"
                                        ? (v.includes("discord.") ? `https://${v}` : `https://discord.com/users/${v}`)
                                        : `https://${v}`;
                                    return (
                                        <a
                                            key={k}
                                            href={href}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`social-${k}`}
                                            className="group flex items-center justify-between p-4 rounded-lg border border-white/5 bg-ink-800/40 hover:border-brand/40 transition-colors"
                                        >
                                            <span className="font-mono text-xs uppercase tracking-wider text-ink-200 group-hover:text-white">
                                                {k}
                                            </span>
                                            <span className="text-brand opacity-0 group-hover:opacity-100 transition-opacity">↗</span>
                                        </a>
                                    );
                                })}
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <form
                            onSubmit={submit}
                            data-testid="contact-form"
                            className="p-7 lg:p-10 rounded-2xl border border-white/5 bg-ink-800/40 backdrop-blur space-y-5"
                        >
                            {/* Honeypot field for bot trapping */}
                            <input
                                type="text"
                                name="hp"
                                value={form.hp}
                                onChange={update("hp")}
                                tabIndex={-1}
                                autoComplete="off"
                                style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0, pointerEvents: "none" }}
                                aria-hidden="true"
                            />

                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field label="Your name" testid="field-name">
                                    <input
                                        required
                                        maxLength={100}
                                        data-testid="contact-input-name"
                                        value={form.name}
                                        onChange={update("name")}
                                        placeholder="Akhmad Ferdiansyah"
                                        className="input"
                                    />
                                </Field>
                                <Field label="Email" testid="field-email">
                                    <input
                                        required
                                        type="email"
                                        maxLength={100}
                                        data-testid="contact-input-email"
                                        value={form.email}
                                        onChange={update("email")}
                                        placeholder="you@example.com"
                                        className="input"
                                    />
                                </Field>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field label="Company (optional)" testid="field-company">
                                    <input
                                        maxLength={100}
                                        data-testid="contact-input-company"
                                        value={form.company}
                                        onChange={update("company")}
                                        placeholder="Acme Inc."
                                        className="input"
                                    />
                                </Field>
                                <Field label="Budget" testid="field-budget">
                                    <select
                                        data-testid="contact-input-budget"
                                        value={form.budget}
                                        onChange={update("budget")}
                                        className="input"
                                    >
                                        <option value="">Select budget…</option>
                                        {budgets.map((b) => (
                                            <option key={b} value={b}>
                                                {b}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                            </div>
                            <Field label="Subject" testid="field-subject">
                                <input
                                    maxLength={200}
                                    data-testid="contact-input-subject"
                                    value={form.subject}
                                    onChange={update("subject")}
                                    placeholder="Project inquiry"
                                    className="input"
                                />
                            </Field>
                            <Field label="Message" testid="field-message">
                                <textarea
                                    required
                                    maxLength={5000}
                                    data-testid="contact-input-message"
                                    value={form.message}
                                    onChange={update("message")}
                                    rows={6}
                                    placeholder="Tell me about your project, goals, and timeline…"
                                    className="input resize-none"
                                />
                            </Field>

                            <div className="flex items-center justify-between pt-2">
                                <p className="text-xs text-ink-300 font-mono">→ Sent securely via Resend</p>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="contact-submit"
                                    className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand text-white font-medium hover:bg-brand-hover transition-colors disabled:opacity-60"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" /> Sending…
                                        </>
                                    ) : done ? (
                                        <>
                                            <CheckCircle2 size={16} /> Send another
                                        </>
                                    ) : (
                                        <>
                                            Send message <Send size={16} className="group-hover:translate-x-0.5 transition-transform" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <style>{`
                .input {
                    width: 100%;
                    background: rgba(10,10,10,0.6);
                    border: 1px solid rgba(255,255,255,0.08);
                    border-radius: 10px;
                    padding: 13px 14px;
                    color: #fff;
                    font-size: 14px;
                    transition: all 0.2s ease;
                    font-family: inherit;
                }
                .input:focus { outline: none; border-color: rgba(255,69,0,0.6); box-shadow: 0 0 0 4px rgba(255,69,0,0.1); }
                .input::placeholder { color: #555; }
                select.input { appearance: none; background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'><path d='m6 9 6 6 6-6'/></svg>"); background-repeat: no-repeat; background-position: right 14px center; padding-right: 38px; }
            `}</style>
        </section>
    );
}

function Field({ label, children, testid }) {
    return (
        <label className="block" data-testid={testid}>
            <span className="mono-label block mb-2">{label}</span>
            {children}
        </label>
    );
}

function ContactRow({ icon: Icon, label, value, href, testid, copyable }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e) => {
        if (!copyable || !value) return;
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(value);
        setCopied(true);
        toast.success(`${label} copied to clipboard!`);
        setTimeout(() => setCopied(false), 2000);
    };

    const inner = (
        <div className="flex items-center justify-between p-4 rounded-lg border border-white/5 bg-ink-800/40 hover:border-brand/40 transition-colors group">
            <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 shrink-0 rounded-md bg-ink-700/60 border border-white/5 flex items-center justify-center text-brand">
                    <Icon size={16} />
                </div>
                <div className="min-w-0">
                    <div className="mono-label">{label}</div>
                    <div className="text-white font-medium group-hover:text-brand transition-colors truncate">{value}</div>
                </div>
            </div>
            {copyable && value && (
                <button
                    type="button"
                    onClick={handleCopy}
                    className="ml-3 shrink-0 px-2.5 py-1.5 rounded-md text-xs font-mono border border-white/10 text-ink-200 hover:text-white hover:border-brand/50 hover:bg-white/5 transition-all flex items-center gap-1.5 bg-black/30"
                    title="Copy to clipboard"
                >
                    {copied ? (
                        <>
                            <Check size={13} className="text-emerald-400" />
                            <span className="text-emerald-400 text-[11px]">Copied</span>
                        </>
                    ) : (
                        <>
                            <Copy size={13} />
                            <span className="text-[11px]">Copy</span>
                        </>
                    )}
                </button>
            )}
        </div>
    );
    if (href)
        return (
            <a href={href} data-testid={testid} className="block">
                {inner}
            </a>
        );
    return <div data-testid={testid}>{inner}</div>;
}
